import { CampaignAuditEntry } from 'hooks/queries/useCampaignAudit';
import { formatDateTime } from 'domains/core/components/DateRangeText/DateRangeText';

/**
 * All possible audit action types
 */
export type AuditAction =
    | 'DRAFT_CREATED' //name displayed
    | 'DRAFT_UPDATED'
    | 'DRAFT_TO_PUBLISHED'
    | 'PUBLISHED' //schedule time
    | 'STATUS_UPDATE'
    | 'AUDIENCE_UPDATED'
    | 'COHORT_UPDATED'
    | 'AUDIENCE_TO_COHORT_UPDATED'
    | 'COHORT_TO_AUDIENCE_UPDATED'
    | 'EMAIL_SUBJECT_UPDATED'
    | 'EMAIL_PREVIEW_TEXT_UPDATED'
    | 'EMAIL_CONTENT_UPDATED'
    | 'SMS_CONTENT_UPDATED'
    | 'VOICE_CONTENT_UPDATED'
    | 'DIRECTMAIL_CONTENT_UPDATED'
    | 'EMAIL_SENDER_PROFILE_UPDATED'
    | 'DIRECTMAIL_SENDER_PROFILE_UPDATED'
    | 'VOICE_SENDER_PROFILE_UPDATED'
    | 'SMS_SENDER_PROFILE_UPDATED';

export type ActionConfig = {
    /**
     * Get the label parts for the action
     * Returns { prefix, bold, suffix } to construct: "{prefix} {bold} {suffix}"
     */
    getLabelParts: (
        entry: CampaignAuditEntry
    ) => {
        prefix: string;
        bold: string;
        suffix?: string;
    };
    /**
     * Whether this action shows status transition chips (old_value -> new_value)
     */
    showsStatusTransition: boolean;
    /**
     * Get the status transition values for the status chips
     * Returns { oldValue, newValue } - can be overridden per action for custom display
     */
    getStatusTransition?: (entry: CampaignAuditEntry) => { oldValue: string | null; newValue: string | null };
    /**
     * Get additional context text to display below the action
     */
    getAdditionalText?: (entry: CampaignAuditEntry) => string | null;
    /**
     * Get value transition text (for non-status changes like audience name changes)
     */
    getValueTransition?: (entry: CampaignAuditEntry) => { from: string; to: string } | null;
};

/**
 * Check if a status transition represents a "resume" action
 */
const isResumeTransition = (oldValue: string | null, newValue: string | null): boolean =>
    oldValue === 'PAUSED' && newValue === 'SENDING';

/**
 * Configuration for each audit action type
 */
export const ACTION_CONFIG: Record<string, ActionConfig> = {
    DRAFT_CREATED: {
        getLabelParts: () => ({
            prefix: 'created',
            bold: 'new Curation',
        }),
        showsStatusTransition: false,
    },

    DRAFT_UPDATED: {
        getLabelParts: () => ({
            prefix: 'updated',
            bold: 'Curation',
        }),
        showsStatusTransition: false,
    },

    DRAFT_TO_PUBLISHED: {
        getLabelParts: () => ({
            prefix: '',
            bold: 'Published Curation',
            suffix: '',
        }),
        showsStatusTransition: true,
        getStatusTransition: (entry) => ({
            oldValue: 'DRAFT',
            newValue: 'SCHEDULED',
        }),
        getAdditionalText: (entry) => {
            if (entry.new_value) {
                return `Scheduled for ${formatDateTime(entry.new_value)}`;
            }
            return null;
        },
    },

    PUBLISHED: {
        getLabelParts: () => ({
            prefix: '',
            bold: 'Published Curation',
            suffix: '',
        }),
        showsStatusTransition: true,
        //TODO: here we have to display the name of the curation.
        // getStatusTransition: (entry) => ({
        //     oldValue: entry.old_value,
        //     newValue: entry.new_value,
        // }),
        getAdditionalText: (entry) => {
            if (entry.new_value) {
                return `Scheduled for ${formatDateTime(entry.new_value)}`;
            }
            return null;
        },
    },

    STATUS_UPDATE: {
        getLabelParts: () => ({
            prefix: 'changed Curation',
            bold: 'status',
        }),
        showsStatusTransition: true,
        getStatusTransition: (entry) => ({
            oldValue: entry.old_value,
            newValue: entry.new_value,
        }),
        getAdditionalText: (entry) => {
            if (isResumeTransition(entry.old_value, entry.new_value)) {
                return 'Curation resumed sending';
            }
            return null;
        },
    },

    AUDIENCE_UPDATED: {
        getLabelParts: () => ({
            prefix: 'changed',
            bold: 'audience',
        }),
        showsStatusTransition: false,
        getValueTransition: (entry) => {
            if (entry.old_value || entry.new_value) {
                return { from: entry.old_value || 'null', to: entry.new_value || 'null' };
            }
            return null;
        },
    },

    COHORT_UPDATED: {
        getLabelParts: () => ({
            prefix: 'changed',
            bold: 'cohort',
        }),
        showsStatusTransition: false,
        getValueTransition: (entry) => {
            if (entry.old_value || entry.new_value) {
                return { from: entry.old_value || 'null', to: entry.new_value || 'null' };
            }
            return null;
        },
    },

    AUDIENCE_TO_COHORT_UPDATED: {
        getLabelParts: () => ({
            prefix: 'changed',
            bold: 'audience to cohort',
        }),
        showsStatusTransition: false,
        getValueTransition: (entry) => {
            if (entry.old_value || entry.new_value) {
                const oldValue = entry.old_value || 'null';
                const newValue = entry.new_value || 'null';
                return { from: `audience : ${oldValue}`, to: `cohort : ${newValue}` };
            }
            return null;
        },
    },

    COHORT_TO_AUDIENCE_UPDATED: {
        getLabelParts: () => ({
            prefix: 'changed',
            bold: 'cohort to audience',
        }),
        showsStatusTransition: false,
        getValueTransition: (entry) => {
            if (entry.old_value || entry.new_value) {
                const oldValue = entry.old_value || 'null';
                const newValue = entry.new_value || 'null';
                return { from: `cohort : ${oldValue}`, to: `audience : ${newValue}` };
            }
            return null;
        },
    },

    EMAIL_SUBJECT_UPDATED: {
        getLabelParts: () => ({
            prefix: 'changed email',
            bold: 'subject',
        }),
        showsStatusTransition: false,
        getValueTransition: (entry) => {
            if (entry.old_value || entry.new_value) {
                return { from: entry.old_value || 'null', to: entry.new_value || 'null' };
            }
            return null;
        },
    },

    EMAIL_PREVIEW_TEXT_UPDATED: {
        getLabelParts: () => ({
            prefix: 'changed email',
            bold: 'preview text',
        }),
        showsStatusTransition: false,
        getValueTransition: (entry) => {
            if (entry.old_value || entry.new_value) {
                return { from: entry.old_value || 'null', to: entry.new_value || 'null' };
            }
            return null;
        },
    },

    EMAIL_CONTENT_UPDATED: {
        getLabelParts: () => ({
            prefix: 'changed email',
            bold: 'content',
        }),
        showsStatusTransition: false,
        getValueTransition: (entry) => {
            if (entry.old_value || entry.new_value) {
                return { from: entry.old_value || 'null', to: entry.new_value || 'null' };
            }
            return null;
        },
    },

    SMS_CONTENT_UPDATED: {
        getLabelParts: () => ({
            prefix: 'changed SMS',
            bold: 'content',
        }),
        showsStatusTransition: false,
        getValueTransition: (entry) => {
            if (entry.old_value || entry.new_value) {
                return { from: entry.old_value || 'null', to: entry.new_value || 'null' };
            }
            return null;
        },
    },

    VOICE_CONTENT_UPDATED: {
        getLabelParts: () => ({
            prefix: 'changed',
            bold: 'voice content',
        }),
        showsStatusTransition: false,
        getValueTransition: (entry) => {
            if (entry.old_value || entry.new_value) {
                return { from: entry.old_value || 'null', to: entry.new_value || 'null' };
            }
            return null;
        },
    },

    DIRECTMAIL_CONTENT_UPDATED: {
        getLabelParts: () => ({
            prefix: 'changed direct mail',
            bold: 'content',
        }),
        showsStatusTransition: false,
        getValueTransition: (entry) => {
            if (entry.old_value || entry.new_value) {
                return { from: entry.old_value || 'null', to: entry.new_value || 'null' };
            }
            return null;
        },
    },

    EMAIL_SENDER_PROFILE_UPDATED: {
        getLabelParts: () => ({
            prefix: 'changed email',
            bold: 'sender profile',
        }),
        showsStatusTransition: false,
        getValueTransition: (entry) => {
            if (entry.old_value || entry.new_value) {
                return { from: entry.old_value || 'null', to: entry.new_value || 'null' };
            }
            return null;
        },
    },

    DIRECTMAIL_SENDER_PROFILE_UPDATED: {
        getLabelParts: () => ({
            prefix: 'changed direct mail',
            bold: 'sender profile',
        }),
        showsStatusTransition: false,
        getValueTransition: (entry) => {
            if (entry.old_value || entry.new_value) {
                return { from: entry.old_value || 'null', to: entry.new_value || 'null' };
            }
            return null;
        },
    },

    VOICE_SENDER_PROFILE_UPDATED: {
        getLabelParts: () => ({
            prefix: 'changed voice',
            bold: 'caller ID',
        }),
        showsStatusTransition: false,
        getValueTransition: (entry) => {
            if (entry.old_value || entry.new_value) {
                return { from: entry.old_value || 'null', to: entry.new_value || 'null' };
            }
            return null;
        },
    },

    SMS_SENDER_PROFILE_UPDATED: {
        getLabelParts: () => ({
            prefix: 'changed sms',
            bold: 'sender ID',
        }),
        showsStatusTransition: false,
        getValueTransition: (entry) => {
            if (entry.old_value || entry.new_value) {
                return { from: entry.old_value || 'null', to: entry.new_value || 'null' };
            }
            return null;
        },
    },
};

/**
 * Get the configuration for an action, with a fallback for unknown actions
 */
export const getActionConfig = (action: string): ActionConfig =>
    ACTION_CONFIG[action] || {
        getLabelParts: () => ({
            prefix: 'performed',
            bold: action.toLowerCase().replace(/_/g, ' '),
        }),
        showsStatusTransition: false,
    };
